import { TextField as MuiTextField } from '@mui/material';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import { motion, useAnimate } from 'motion/react';
import React, {
  forwardRef,
  memo,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from '../theme';
import mergeRefs from '../utils/merge-refs';
import { NumericFormatMoney } from './NumericFormatMoney';

export interface TextFieldProps
  extends React.ComponentProps<typeof MuiTextField> {
  disabledByDefault?: boolean;
  isNumberInput?: boolean;
  isMonetaryInput?: boolean;
  /**
   * Monetary inputs only: an empty field reports `null` and a `null` value
   * renders empty with the placeholder, instead of reading as the amount 0.
   */
  shouldAllowEmptyAmount?: boolean;
  allowedToEditAndCreate?: boolean;
  acceptOnlyPositiveNumbers?: boolean;
  decimalScale?: number;
  endAdornment?: StandardInputProps['endAdornment'];
  /**
   * Inline character counter rendered as a subtle "12/200" end adornment
   * that fades out before the typed text would collide with it, then fades
   * back in when the text shrinks. Requires `maxLength`.
   */
  showCharacterCount?: boolean;
  /**
   * Hard cap on input length. When set, also enforces `inputProps.maxLength`.
   * Pair with `showCharacterCount` to display the inline counter.
   */
  maxLength?: number;
}

// Reveal the counter when the input is focused OR the user is within the
// last 20% of the allowed length — proactive warning + clean idle state.
const NEAR_LIMIT_RATIO = 0.8;

const CharacterCounter = ({
  value,
  maxLength,
  inputRef,
  atLimitColor,
}: {
  value: string;
  maxLength: number;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  atLimitColor: string;
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [scope, animate] = useAnimate<HTMLSpanElement>();

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const onFocus = () => setIsFocused(true);
    const onBlur = () => setIsFocused(false);
    input.addEventListener('focus', onFocus);
    input.addEventListener('blur', onBlur);
    if (document.activeElement === input) setIsFocused(true);
    return () => {
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
    };
  }, [inputRef]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // Only react to printable single-character key presses while no
      // selection is active — covers typed input but ignores backspace,
      // navigation, and shortcuts (copy/paste etc).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return;
      if (input.selectionStart !== input.selectionEnd) return;
      if (input.value.length < maxLength) return;
      if (!scope.current) return;

      void animate(
        scope.current,
        { x: [0, -3, 3, -2, 2, 0] },
        { duration: 0.3, ease: 'easeOut' }
      );
    };

    input.addEventListener('keydown', onKeyDown);
    return () => input.removeEventListener('keydown', onKeyDown);
  }, [inputRef, maxLength, animate, scope]);

  const isNearLimit = value.length / maxLength >= NEAR_LIMIT_RATIO;
  const isAtLimit = value.length >= maxLength;
  const isVisible = isFocused || isNearLimit;

  return (
    <motion.span
      ref={scope}
      initial={false}
      animate={{ opacity: isVisible ? (isAtLimit ? 1 : 0.6) : 0 }}
      transition={{ type: 'spring', visualDuration: 0.18, bounce: 0 }}
      aria-live="polite"
      aria-label={`${value.length} of ${maxLength} characters used`}
      style={{
        fontSize: '0.75rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        marginLeft: 8,
        userSelect: 'none',
        color: isAtLimit ? atLimitColor : 'inherit',
      }}
    >
      {value.length}/{maxLength}
    </motion.span>
  );
};

export default memo(
  forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
    {
      disabledByDefault,
      isNumberInput,
      isMonetaryInput,
      shouldAllowEmptyAmount,
      sx,
      allowedToEditAndCreate = true,
      InputProps,
      value,
      decimalScale,
      acceptOnlyPositiveNumbers,
      endAdornment,
      inputMode,
      inputProps,
      maxLength,
      showCharacterCount,
      ...rest
    },
    ref
  ) {
    const [theme] = useTheme();
    const textInputRef = useRef<HTMLInputElement | null>(null);
    if (isMonetaryInput && !isNumberInput) {
      throw new Error(
        "can't render a monetary field if its not a number input. You must pass both isMonetaryInput=true and isNumberInput set to true."
      );
    }

    // Refer to https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
    // and https://github.com/mui/material-ui/issues/19154#issuecomment-1601017499
    // and https://github.com/mui/material-ui/issues/7960
    // and https://stackoverflow.com/questions/9712295/disable-scrolling-on-input-type-number
    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (!isNumberInput) {
        return;
      }

      if (acceptOnlyPositiveNumbers && e.key === '-') {
        e.preventDefault();
      }

      if (e.key === 'e' || e.key === 'E' || e.key === '+') {
        e.preventDefault();
      }
    };

    const isReadOnly = disabledByDefault || !allowedToEditAndCreate;
    const variant = rest.variant ?? 'outlined';
    const supportsDisableUnderline =
      variant === 'standard' || variant === 'filled';

    const stringValue = typeof value === 'string' ? value : '';
    const effectiveCounterMax =
      showCharacterCount && typeof maxLength === 'number' && maxLength > 0
        ? maxLength
        : null;
    const errorMainColor = theme.palette.error.main;

    const composedEndAdornment = useMemo(() => {
      const counter =
        effectiveCounterMax !== null ? (
          <CharacterCounter
            value={stringValue}
            maxLength={effectiveCounterMax}
            inputRef={textInputRef}
            atLimitColor={errorMainColor}
          />
        ) : null;
      if (!endAdornment && !counter) return undefined;
      if (!endAdornment) return counter;
      if (!counter) return endAdornment;
      return (
        <>
          {endAdornment}
          {counter}
        </>
      );
    }, [endAdornment, effectiveCounterMax, stringValue, errorMainColor]);

    const mergedInputProps = useMemo(() => {
      const next: Record<string, unknown> = {
        ...inputProps,
        style: {
          // Make disabled input fields text slightly brighter on webkit
          WebkitTextFillColor:
            theme.palette.mode === 'light'
              ? 'rgba(0, 0, 0, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          ...(typeof inputProps?.style === 'object' ? inputProps.style : {}),
        },
        inputMode: inputMode ?? (isNumberInput ? 'numeric' : 'text'),
        ...(isMonetaryInput && { decimalScale, shouldAllowEmptyAmount }),
      };
      if (typeof maxLength === 'number') {
        next.maxLength = maxLength;
      }
      return next;
    }, [
      inputProps,
      inputMode,
      isNumberInput,
      isMonetaryInput,
      shouldAllowEmptyAmount,
      decimalScale,
      maxLength,
      theme.palette.mode,
    ]);

    return (
      <MuiTextField
        fullWidth
        sx={{
          ...sx,
        }}
        InputProps={{
          readOnly: isReadOnly,
          ...(supportsDisableUnderline && {
            disableUnderline: isReadOnly,
          }),
          ...InputProps,
          inputComponent: isMonetaryInput
            ? NumericFormatMoney
            : InputProps?.inputComponent,
          ...(composedEndAdornment != null && {
            endAdornment: composedEndAdornment,
          }),
        }}
        inputProps={mergedInputProps}
        value={value}
        type="text"
        onKeyDown={handleKeyDown}
        {...rest}
        inputRef={mergeRefs([ref, textInputRef, rest.inputRef])}
        InputLabelProps={{ shrink: true, ...rest.InputLabelProps }}
      />
    );
  })
);
